import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import products from '../data/products';
import './Order.css';

function Order() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const preselectedId = queryParams.get('product');
  
  // Track if we've already processed the URL parameter
  const processedUrlParam = useRef(false);
  
  const allProducts = [
    ...products.smoothies,
    ...products.juices,
    ...products.proteinBalls
  ];

  // Use localStorage to persist cart between page navigations
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  
  // Load customer info from localStorage if available
  const [customerInfo, setCustomerInfo] = useState(() => {
    const savedInfo = localStorage.getItem('customerInfo');
    return savedInfo ? JSON.parse(savedInfo) : {
      name: '',
      phone: '',
      zone: '',
      address: ''
    };
  });
  
  // State for showing notification when products are added
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    product: null
  });
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
    
    // Dispatch custom event for Header component to update cart count
    window.dispatchEvent(new CustomEvent('cartUpdated'));
  }, [cart]);
  
  // Handle adding a preselected product from URL, but only once when the component mounts
  useEffect(() => {
    const handlePreselectedProduct = () => {
      // Skip if we've already processed this or there's no product ID
      if (processedUrlParam.current || !preselectedId) {
        return;
      }
      
      // Find the product
      const selectedProduct = allProducts.find(p => p.id === preselectedId);
      if (!selectedProduct) {
        return;
      }
      
      // Get the most current cart from localStorage
      const currentCartJSON = localStorage.getItem('cart');
      const currentCart = currentCartJSON ? JSON.parse(currentCartJSON) : [];
      
      // Check if the product is already in the cart
      const existingItem = currentCart.find(item => item.product.id === preselectedId);
      
      if (!existingItem) {
        // Add the product to cart
        const newCart = [...currentCart, { product: selectedProduct, quantity: 1 }];
        setCart(newCart);
        localStorage.setItem('cart', JSON.stringify(newCart));
      }
      
      // Mark as processed and remove from URL
      processedUrlParam.current = true;
      navigate('/cart', { replace: true });
    };
    
    // Run the handler after a short delay to ensure component is fully mounted
    setTimeout(handlePreselectedProduct, 0);
    
    // Run only once when component mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedId, navigate, allProducts]);
  
  const addToCart = (product) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.product.id === product.id);
      if (existingItem) {
        // Show notification for increasing quantity
        setNotification({
          show: true,
          message: `Added another ${product.name} to your cart!`,
          product: product
        });
        
        return prevCart.map(item => 
          item.product.id === product.id 
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      } else {
        // Show notification for new item
        setNotification({
          show: true,
          message: `${product.name} added to your cart!`,
          product: product
        });
        
        return [...prevCart, { product, quantity: 1 }];
      }
    });
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', product: null });
    }, 3000);
  };
  
  const removeFromCart = (productId) => {
    setCart(prevCart => {
      const updatedCart = prevCart.filter(item => item.product.id !== productId);
      return updatedCart;
    });
  };
  
  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      // If quantity is less than 1, remove the item completely
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart => {
      const updatedCart = prevCart.map(item => 
        item.product.id === productId 
          ? { ...item, quantity: newQuantity } 
          : item
      );
      return updatedCart;
    });
  };
  
  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0).toFixed(2);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Save customer info when it changes
  useEffect(() => {
    localStorage.setItem('customerInfo', JSON.stringify(customerInfo));
  }, [customerInfo]);

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare order data to send to backend
      const order = {
        items: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity
        })),
        customer: customerInfo,
        deliveryAddress: `${customerInfo.zone} - ${customerInfo.address}`,
        total: parseFloat(calculateTotal()),
        status: 'pending'
      };
      
      // Import here to avoid circular dependency
      const { createOrder } = await import('../services/api');
      
      // Send order to backend
      const createdOrder = await createOrder(order);
      console.log('Order created:', createdOrder);
      
      // Show success message
      alert('Your order has been placed successfully! In the future, Wave Mobile and Orange Money payment options will be available for your convenience.');
      
      // Clear cart in state and localStorage
      setCart([]);
      localStorage.removeItem('cart');
      
      // We keep customer info in localStorage for future orders
      
      navigate('/');
    } catch (error) {
      console.error('Failed to submit order:', error);
      alert('There was a problem submitting your order. Please try again.');
    }
  };
  
  return (
    <div className="order-page">
      {/* Notification toast */}
      {notification.show && (
        <div className="notification-toast">
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            {notification.product && 
              <span className="notification-details">
                ${notification.product.price.toFixed(2)}
              </span>
            }
          </div>
          <div className="notification-progress"></div>
        </div>
      )}
      
      <div className="order-header">
        <h1>Your Cart</h1>
        <p>Review your items and complete your order for delivery.</p>
      </div>
      
      <div className="order-content">
        <div className={`order-summary ${notification.show ? 'highlight-cart' : ''}`} style={{width: '100%', maxWidth: '800px', margin: '0 auto'}}>
          <h2>Cart Items {cart.length > 0 && <span className="cart-count">{cart.length}</span>}</h2>
          
          {cart.length === 0 ? (
            <div className="empty-cart">
              <p>Your cart is empty. Add some items to get started!</p>
              <button 
                onClick={() => navigate('/menu')} 
                className="add-button" 
                style={{maxWidth: '200px', margin: '20px auto 0'}}
              >
                Go to Menu
              </button>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {cart.map(item => (
                  <div className="cart-item" key={item.product.id}>
                    <div className="item-details">
                      <h4>{item.product.name}</h4>
                      <p>${item.product.price.toFixed(2)} each</p>
                    </div>
                    <div className="item-quantity">
                      <button 
                        onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                        className="quantity-btn"
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                        className="quantity-btn"
                      >
                        +
                      </button>
                    </div>
                    <div className="item-total">
                      ${(item.product.price * item.quantity).toFixed(2)}
                    </div>
                    <button 
                      onClick={() => removeFromCart(item.product.id)}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="order-total">
                <strong>Total: ${calculateTotal()}</strong>
              </div>
              
              <button 
                onClick={() => navigate('/menu')} 
                className="add-button" 
                style={{maxWidth: '200px', marginBottom: '20px'}}
              >
                Continue Shopping
              </button>
              
              <form onSubmit={handleSubmitOrder} className="customer-info">
                <h3>Delivery Information</h3>
                <p className="delivery-note">Your items will be delivered to your location in Senegal. Fields marked with an asterisk (*) are required.</p>
                
                <div className="form-group">
                  <label htmlFor="name" className="required-field">Full Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone" className="required-field">Phone Number</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="zone" className="required-field">Delivery Zone</label>
                  <select
                    id="zone"
                    name="zone"
                    value={customerInfo.zone}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a delivery zone</option>
                    <option value="Aéroport">Aéroport</option>
                    <option value="Almadies">Almadies</option>
                    <option value="Amitié">Amitié</option>
                    <option value="BA 160 / Quartier Colonel Geille">BA 160 / Quartier Colonel Geille</option>
                    <option value="BCEAO Yoff">BCEAO Yoff</option>
                    <option value="Bel Air">Bel Air</option>
                    <option value="Biscuiterie">Biscuiterie</option>
                    <option value="Cambérène">Cambérène</option>
                    <option value="Castor">Castor</option>
                    <option value="Centenaire">Centenaire</option>
                    <option value="Centre Ville">Centre Ville</option>
                    <option value="Cité Alioune Sow">Cité Alioune Sow</option>
                    <option value="Cité Keur Gorgui">Cité Keur Gorgui</option>
                    <option value="Cité Mbackiyou Faye">Cité Mbackiyou Faye</option>
                    <option value="Colobane">Colobane</option>
                    <option value="Croisement Cambérène">Croisement Cambérène</option>
                    <option value="Dakar Academy Maristes">Dakar Academy Maristes</option>
                    <option value="Dakar Academy West">Dakar Academy West</option>
                    <option value="Dalifort Foirail">Dalifort Foirail</option>
                    <option value="Damel">Damel</option>
                    <option value="Diamalaye">Diamalaye</option>
                    <option value="Dieuppeul / Derklé">Dieuppeul / Derklé</option>
                    <option value="Fann Hock">Fann Hock</option>
                    <option value="Fann Résidence">Fann Résidence</option>
                    <option value="Fass">Fass</option>
                    <option value="Gibraltar">Gibraltar</option>
                    <option value="Grand Dakar">Grand Dakar</option>
                    <option value="Grand Yoff">Grand Yoff</option>
                    <option value="Gueule Tapée">Gueule Tapée</option>
                    <option value="Hann Marinas">Hann Marinas</option>
                    <option value="HLM">HLM</option>
                    <option value="HLM Grand Médine">HLM Grand Médine</option>
                    <option value="International School of Dakar">International School of Dakar</option>
                    <option value="ISRA">ISRA</option>
                    <option value="Jet d'eau">Jet d'eau</option>
                    <option value="Liberté 1-2-3-4-5">Liberté 1-2-3-4-5</option>
                    <option value="Liberté 6">Liberté 6</option>
                    <option value="Mamelles">Mamelles</option>
                    <option value="Maristes">Maristes</option>
                    <option value="Medina">Medina</option>
                    <option value="Mermoz">Mermoz</option>
                    <option value="Mixta">Mixta</option>
                    <option value="Ngor">Ngor</option>
                    <option value="Niarry Tally">Niarry Tally</option>
                    <option value="Nord Foire">Nord Foire</option>
                    <option value="Ouakam">Ouakam</option>
                    <option value="Ouest Foire">Ouest Foire</option>
                    <option value="Parcelles après Dior">Parcelles après Dior</option>
                    <option value="Patte d'oie">Patte d'oie</option>
                    <option value="Point E">Point E</option>
                    <option value="Rebeuss">Rebeuss</option>
                    <option value="Sacré Cœur 3 Extension">Sacré Cœur 3 Extension</option>
                    <option value="Sacré-Coeur">Sacré-Coeur</option>
                    <option value="Scat Urbam">Scat Urbam</option>
                    <option value="SICAP Baobab / Karak">SICAP Baobab / Karak</option>
                    <option value="SICAP Foire">SICAP Foire</option>
                    <option value="Sipres">Sipres</option>
                    <option value="Sodida">Sodida</option>
                    <option value="U15">U15</option>
                    <option value="U24-25">U24-25</option>
                    <option value="U26">U26</option>
                    <option value="UCAD">UCAD</option>
                    <option value="Virage">Virage</option>
                    <option value="Yarakh">Yarakh</option>
                    <option value="Yoff">Yoff</option>
                    <option value="Yoff Apecsy">Yoff Apecsy</option>
                    <option value="Zone A / B">Zone A / B</option>
                    <option value="Zone de captage">Zone de captage</option>
                    <option value="Zone Industrielle 2 à 8 km">Zone Industrielle 2 à 8 km</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address" className="required-field">Additional Address Details</label>
                  <textarea 
                    id="address" 
                    name="address" 
                    value={customerInfo.address}
                    onChange={handleInputChange}
                    placeholder="Enter apartment number, building name, street, or landmarks"
                    required
                    rows="3"
                  ></textarea>
                </div>
                
                <div className="payment-info">
                  <p>Coming soon: Wave Mobile and Orange Money payment options</p>
                </div>
                
                <button type="submit" className="submit-order">
                  Place Order for Delivery
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Order;